// Static changelog — one entry per shipped feature / fix. Append to the
// top when shipping something user-visible. Date format is YYYY-MM-DD.
//
// Why static (not DB-driven): adding an entry is a code change anyway
// (the feature itself), so versioning the changelog alongside the code
// keeps them in sync. No admin UI required, no DB roundtrip on every
// page load.
//
// Render: see `admin-ui/src/routes/changelog.tsx`. The TopNav shows an
// unread dot when the newest entry's date is newer than the user's
// `pa-changelog-last-seen` localStorage timestamp.

export type ChangelogCategory = 'feature' | 'improvement' | 'fix';

export interface ChangelogEntry {
  /** YYYY-MM-DD. Used for the unread indicator + sort order. */
  date: string;
  category: ChangelogCategory;
  title: string;
  /** Plain-text description, 1–3 sentences. Render as a paragraph below
   *  the title. Avoid markdown — keep it readable in raw form too. */
  body: string;
  /** Optional sub-bullets for richer entries (e.g. "How to use it"). */
  details?: string[];
}

export const CHANGELOG: ChangelogEntry[] = [
  {
    date: '2026-10-21',
    category: 'improvement',
    title: 'Pop-up messages no longer cover the bell',
    body:
      'Messages like "Reply sent" now appear just below the top bar instead of on top of it, so the notification bell ' +
      'is always clickable. The notifications panel opens in front of any message still showing.',
  },
  {
    date: '2026-10-17',
    category: 'improvement',
    title: 'The top bar fits smaller screens',
    body:
      'On laptop-sized windows the top bar no longer wraps "My Day" and "What\'s new" onto two lines or pushes Search, ' +
      'Compose and your avatar off the right edge. Parts now step back in order as the window narrows.',
    details: [
      'Below about 1440px the email next to your avatar is hidden. Open the avatar menu to see it.',
      'Below about 1280px Search and Compose show as icons only.',
      'Below about 1100px the links move into the menu button at the top right.',
    ],
  },
  {
    date: '2026-10-17',
    category: 'fix',
    title: 'Ticket rows fit the screen again',
    body:
      'After the AI summary was added to each row, long summaries pushed the status and age columns off the right ' +
      'edge of the Tickets list and My Day. Rows now stay inside the window, with the columns lined up as before.',
    details: [
      'Applies everywhere tickets are listed: Tickets, My Day (including Follow-ups), the FYI tab and search results.',
      'The summary is cut off with "…" where it would run out of room. Hover the row to read all of it.',
    ],
  },
  {
    date: '2026-10-17',
    category: 'feature',
    title: 'Templates that do the next step for you',
    body:
      'A reply template can now also resolve the ticket, snooze it, change its priority, hand it to a teammate or set ' +
      'a follow-up reminder, once your reply has actually gone out.',
    details: [
      'Edit a template and pick "After sending". Templates that have one show "Then: …" in the list.',
      'Insert the template in a reply and a "Then: …" chip appears next to Send. Click its x to leave the action out for this reply.',
      'Undo, or a reply that fails to send, cancels the action. If the action itself is refused you get the usual message with a Retry.',
      '"Remind me in N days" sets a reminder for 9:00 that day, only if the client has not replied.',
      'The chip only appears for actions your role allows in that inbox.',
    ],
  },
  {
    date: '2026-10-17',
    category: 'feature',
    title: 'Remind me to follow up',
    body:
      'Set a reminder on any ticket: "remind me in 2 days if the client hasn\'t replied". Unlike Snooze, the ticket ' +
      'stays where it is. When the time comes you get a notification, and the ticket shows at the top of My Day.',
    details: [
      'Click Remind on a ticket (or press Ctrl+K and type "remind me in 2 days"). Pick tomorrow, 2 days, a week, or any time, and add a note to yourself.',
      '"Only if the client hasn\'t replied" (on by default) drops the reminder if they write back first, since you will hear about that anyway.',
      'A small alarm icon marks tickets that have a reminder. My Day has a Follow-ups section with a Done button.',
      'Sending a reply on a ticket whose reminder has already fired marks it done. You can switch the reminder email off under Settings.',
    ],
  },
  {
    date: '2026-10-17',
    category: 'feature',
    title: 'A warning when someone gets there first',
    body:
      'If the client writes again, or a teammate sends a reply, while you are writing yours, the reply box ' +
      'now tells you, and Send asks before going out. The ticket you have open also updates by itself.',
    details: [
      'An amber strip says what happened ("Kurt wrote again. Liz has already replied."). Show jumps to the message; Got it clears the strip.',
      'Send asks "Send anyway?" while the strip is showing, and Review first keeps what you wrote.',
      'Your own reply landing a few seconds after you started the next one never counts.',
      'Once you start typing, teammates see "Liz is writing a reply" on that ticket, so two people do not answer the same email.',
      'New messages appear on an open ticket within about 15 seconds, no reload needed.',
    ],
  },
  {
    date: '2026-10-17',
    category: 'feature',
    title: 'Unread tickets show in bold',
    body:
      'A ticket where the client has written since you last opened it now shows in bold with a dot, on the ' +
      'Dashboard and on My Day. It is per person: opening a ticket clears it for you only.',
    details: [
      'The Unread button in the filter bar lists only those tickets, with a count.',
      'Opening a ticket marks it seen. "Mark as unread" (the ⋯ menu on the ticket, or Ctrl+K) puts the bold back so you can come back to it.',
      'Only new client messages from today on count, so you will not open to hundreds of bold rows. Automated mail in the FYI tab is never marked unread.',
    ],
  },
  {
    date: '2026-10-17',
    category: 'improvement',
    title: 'Work through your queue without going back to the list',
    body:
      'A ticket you open from a list now knows where it came from. Resolve it, snooze it or send your reply ' +
      'and the next ticket opens by itself. You can also step through with [ and ], change a ticket\'s priority ' +
      'from its badge, reopen a resolved ticket, and run actions from Ctrl+K.',
    details: [
      'The strip above a ticket reads "3 of 57 · Unassigned". The switch next to it turns "open the next ticket" off.',
      '[ and ] go to the previous and next ticket. They work from an empty reply box too.',
      'Click the priority badge to pick P1 to P4. Resolved tickets have a Reopen button.',
      'Every row and every ticket now shows the AI\'s one-line read of what the client wants, and flags a frustrated or angry tone.',
      'Press Ctrl+K on a ticket and type an action: resolve, snooze tom, assign liz, p1, reopen, next.',
    ],
  },
  {
    date: '2026-10-17',
    category: 'feature',
    title: 'FYI tab: automated mail stops cluttering your lists',
    body:
      'Receipts, alerts and newsletters from senders marked as automated (Stripe, Twilio, Zoom and the like) ' +
      'now wait in their own FYI tab. They are out of All open, Mine, Unassigned and My Day, post no Slack card ' +
      'and raise no SLA alerts, so what is left is mail a person has to act on.',
    details: [
      'Open the FYI tab and press Clear all (N) to resolve everything in it. You are asked first, and told if some belong to a teammate.',
      'Under the strip, "N more senders look automated" lists senders with many emails and no reply: Move to FYI sends all their mail there, Not a robot hides the suggestion.',
      'On a ticket, "Mark as automated" / "Not automated" next to the sender does the same for one sender.',
      'If you claim or reply to a ticket in FYI, it becomes a normal ticket and moves to your lists.',
      'My Day shows a one-line card with how many are waiting.',
    ],
  },
  {
    date: '2026-10-16',
    category: 'fix',
    title: 'No ticket gets left off the list any more',
    body:
      'A ticket that was created a while ago — say a long thread the client keeps replying to — could be ' +
      'missing from My Day and from the Dashboard tabs even though it was still in progress, because the ' +
      'lists only looked at the newest 500 tickets. Every list now reads the full set, so nothing drops ' +
      'off because it is old.',
    details: [
      'My Day: My queue, Needs attention, Waiting on client and the "My open" count include your older tickets, and so does a teammate\'s queue while you cover for them.',
      'Dashboard: the tab numbers count everything, and more tickets load as you scroll to the bottom of the list.',
      'Sorting (for example Oldest first) now sorts across all your tickets, not just the ones already on screen.',
      'The inbox "new" badges are exact even with more than a thousand new tickets.',
    ],
  },
  {
    date: '2026-10-16',
    category: 'improvement',
    title: 'A "Sending…" message stays on screen until your email is sent',
    body:
      'After you press Send, one message in the corner now follows your email all the way: ' +
      '"Sending in 5s… Undo", then "Sending…" with a spinner, then "Reply sent". It stays on every ' +
      'page while the email is going out, so you can tell when it is safe to close the tab.',
    details: [
      'With attachments it says which file it is on, e.g. "Uploading 1 of 2 files…".',
      'If something goes wrong the same message turns red with a Retry button, and Retry never sends the email twice.',
      'Undo turns the same message into "Send cancelled — nothing was sent".',
    ],
  },
  {
    date: '2026-10-15',
    category: 'feature',
    title: 'Send returns straight away — keep working while it goes out',
    body:
      'When you press Send, the reply box clears at once and your message appears in the conversation ' +
      'as "Sending…". You can start the next reply or go to any other page; it keeps sending in the ' +
      'background. The same goes for a new ticket email: the window closes immediately.',
    details: [
      'You still get 5 seconds to change your mind. The "Sending in 5s… Undo" message follows you to whatever page you are on, and Undo puts your text, recipients and files back.',
      'If a send fails you are told, wherever you are, with Retry. Retrying never sends it twice.',
      'Two quick replies to the same ticket always go out in the order you sent them.',
      'Closing the tab while something is still going out asks you first.',
    ],
  },
  {
    date: '2026-10-15',
    category: 'improvement',
    title: 'Ticket actions respond instantly',
    body:
      'Resolve, claim, assign, snooze, escalate, wake up, dismiss, archive and merge now show their ' +
      'result the moment you click, instead of after a 1–3 second wait. The change is confirmed with ' +
      'the server in the background; if it is refused the ticket goes back and you can retry.',
  },
  {
    date: '2026-10-13',
    category: 'feature',
    title: 'Roles and access levels',
    body:
      'Each person now has a role that decides what they can do, and a separate role for each inbox, ' +
      'so someone can be an Agent in Support and read-only in Client Services. Owners and Admins can ' +
      'see everything; everyone else only sees what their role allows. Buttons you cannot use are hidden.',
    details: [
      'Team: pick a person to set their role and a role for each inbox, add an expiry date for temporary access, or invite someone with a role and inboxes already chosen.',
      'Roles: the built-in roles (Owner, Admin, Manager, Agent, Limited and the inbox roles) are locked. Clone one to make your own and tick exactly the permissions you want.',
      'Who can do what: a grid of every role against every permission, and a search for "who can reply in this inbox?".',
      'Every change to someone\'s access is recorded in the access log on the Team page.',
      'Under the hood the same rules are now enforced by the database, the AI assist and the ticket actions, not only the screen, and a leak that let the public key read inbox data was closed.',
    ],
  },
  {
    date: '2026-10-13',
    category: 'feature',
    title: 'Search rebuilt: find any ticket, anywhere in the conversation',
    body:
      'One search box now looks through subjects, senders, every message, internal notes, ' +
      'attachments and contacts, finds partial words, and shows you where each match is. ' +
      'It also fixes search showing nothing, and a leak that let search see inboxes you ' +
      'should not.',
    details: [
      'Type part of a word ("invo" finds "invoice"), an email, a name, or a ticket ID (the 8 characters shown in the ticket header). The old "Deep search" button is gone: the search always looks everywhere.',
      'Each result shows why it matched (message from the client, a team reply, an internal note, an attachment, To/CC…) with your words highlighted. Click it to open the ticket with that word already found in the conversation.',
      'Tabs, filters and the date range now apply to the search itself, and the tab counts describe your matches. If nothing matches in "All open", the empty page tells you how many matches are in Archived or All and jumps there in one click.',
      'Shortcuts: "exact phrase", -leave-out, refund OR credit, from:liz@acme.com, to:, assignee:me, status:closed, priority:p1, category:billing, has:attachment, after:2026-09-01, before:2026-10-01, is:archived. Click the ? in the search box for examples.',
      'Your last 10 searches appear when you click the empty search box. The search is kept in the page address, so a reload, a shared link or a saved view brings it back.',
      'Press Ctrl+F (⌘F) inside a ticket to find text in the conversation, with a match counter and next/previous. In the reply box it still opens the browser\u2019s own find.',
      'Ctrl+K now finds any ticket in the current inbox (not just the newest 500), and a Knowledge Base result opens that article.',
      'Fixed: search results used to include tickets from inboxes you do not have access to, and the public site key could read them. Confidential inboxes are now properly hidden from search.',
      'Fixed: typing in the search box no longer clears your selected tickets, and a failed search now says so with a Retry button instead of looking empty.',
      'Fixed: searching a contact\u2019s tickets by subject ignored every letter "s".',
    ],
  },
  {
    date: '2026-10-13',
    category: 'feature',
    title: 'Draft and Polish now take direction, and you can undo them',
    body:
      'Tell the AI what to say or how to change your text, restyle in one click, ' +
      'and save the instructions you use a lot. Open it with Guide AI in the reply ' +
      'box or the new email window. Nothing is ever sent automatically.',
    details: [
      'Type a short instruction, like "refund approved, lands Friday", then press Draft (write it) or Polish (rewrite what you have). Press Enter in the box to do whichever fits: Polish when you have written something, Draft when the body is empty.',
      'One-click Restyle: Fix grammar only, Shorter, Friendlier, More formal, Firmer, Plain English. Your facts, numbers, links and commitments are kept.',
      'Mine: save an instruction you reuse (for example "ask for a screenshot and the login email") as a one-click chip. Only you see your saved prompts.',
      'Draft now uses what you already typed as your notes instead of throwing it away, and it reads the ticket\u2019s internal notes and the suggested Knowledge Base articles. It writes in your own voice, based on your recent replies.',
      'Undo: every AI change can be undone from the toast or the Undo button, up to five steps. The old "Cmd+Z" hint was never true for Polish.',
      'Fixed: Polish used to remove pasted screenshots and links from your reply. They now stay, and if the AI drops one it is put back at the end.',
      'The new email window also has Draft now, so you can write a first message from a short instruction.',
    ],
  },
  {
    date: '2026-10-09',
    category: 'feature',
    title: 'Contacts: find anyone, and a proper page for each contact',
    body:
      'The Contacts page now lists everyone who has emailed you with search, filters, ' +
      'sorting, stats and a CSV download, and each contact page has quick actions, ' +
      'real numbers, shared notes and their Patients on Demand details.',
    details: [
      'New list: search by name, email or company; filter by client / prospect, tier, inbox, open tickets or hand-edited; sort by recent activity, most tickets, most open, name or needs attention. Filters live in the address bar, so a view can be bookmarked or shared.',
      'Robots like Stripe, Zoom and Twilio are now hidden by default (turn on "Show automated senders" to see them). On any contact, use ⋯ → Mark as automated sender for others.',
      'Contact page: Email opens Compose already addressed to them, Call dials their number, and Patients on Demand opens their record there. A 12-month activity chart, response times, SLA breach rate and recent sentiment sit at the top, and the ticket list can be filtered by status, inbox and subject.',
      'Notes are now a shared feed: add as many as you like, pin the important ones, and edit or delete your own (admins can manage any). Adding a note no longer pauses Patients on Demand updates.',
      'Fixed: the list used to show only 200 of your contacts, big senders had wrong totals, and a comma in the search box broke it. You now only see contacts who emailed an inbox you can access.',
      'Fixed: contacts were not refreshing from Patients on Demand when they emailed again. They now update, and a Patients on Demand hiccup can no longer turn a client into a prospect on a ticket.',
    ],
  },
  {
    date: '2026-10-09',
    category: 'feature',
    title: 'Reports: a much bigger, more accurate report page',
    body:
      'Reports now shows exact numbers for any date range, compares every figure ' +
      'with the previous period, and adds speed, SLA, backlog, team and "busiest ' +
      'hours" views. Click a big number or a row to open those tickets.',
    details: [
      'Fixed: the old page silently counted only about half of a busy month (the server returns at most 1,000 rows), put days on UTC instead of Sydney, left today off the chart, and counted dismissed spam as "resolved".',
      'New: median and p90 first-reply and resolution times, reply rate, SLA breach rate over time, open tickets over time, a Priority / Category / Sentiment / Client tier / Inbox breakdown, and a Sydney weekday × hour heat map of when tickets arrive.',
      'New: a Team performance table (assigned, resolved, open now, reply and resolution times, SLA breach rate, replies sent, notes added) — everyone can see it. Sort any column, filter by name, or click a name to open their tickets.',
      'New: a live backlog panel (age of open tickets, overdue, due within 8 hours, unassigned, the oldest tickets) and your top ten clients.',
      'Filters now include inbox, team, person, priority and category, and live in the address bar so a view can be bookmarked or shared. Every chart has a Table view, and everything exports to CSV.',
      'Dismissed tickets (spam / no action) are now counted separately and never as resolved. Tap the ⓘ next to any number for exactly how it is calculated.',
    ],
  },
  {
    date: '2026-09-21',
    category: 'feature',
    title: 'Notifications: category tabs, and mark as unread',
    body:
      'The notifications bell and the Notifications page now have tabs — All, ' +
      'Replies, Assigned, Notes, Resolved, Alerts and Team — each with its own ' +
      'unread count, plus an “Unread only” switch. You can also mark any ' +
      'notification unread, so it stays put until you get back to it.',
    details: [
      'Want to see who replied? Open the Replies tab. Want to check what got closed? Resolved. All still shows everything, in order.',
      'Hover a notification to reveal the mark read / mark unread button; on a phone it is always visible. Clicking the notification itself still opens the ticket and marks it read, as before.',
      'A notification you mark unread stays unread until you click it, mark it read again, or open its ticket. The bell badge and the browser tab count include it in the meantime.',
      '“Mark all read” now only clears the tab you are on. Use it on All to clear everything.',
      'The bell panel works with the keyboard now: Tab moves through the tabs and notifications, and Escape closes it.',
      'On the Notifications page the tab and switch are kept in the address, so a refresh or a shared link lands on the same view.',
    ],
  },
  {
    date: '2026-08-17',
    category: 'feature',
    title: 'Pick your own colour theme — 22 to choose from',
    body:
      'Settings now has a Colour theme section with 22 themes, grouped by ' +
      'family: neutrals (Neutral, Graphite, High contrast, Midnight), cool ' +
      'blues (Slate, Ocean, Sky, Steel, Nord), greens (Forest, Mint, Moss), ' +
      'warm papers (Sepia, Sandstone, Espresso, Gruvbox, Solarized) and ' +
      'purples and pinks (Lavender, Plum, Dracula, Rose, Crimson). Each has ' +
      'both a light and a dark version, so your theme works with the ' +
      'light/dark switch rather than replacing it. Your choice is yours ' +
      'alone; nobody else sees it.',
    details: [
      'Settings → Colour theme shows a live preview of every option, so you can compare them before committing. Picking one applies it straight away — there is no Save button to hunt for.',
      'In a hurry: the same themes are a row of swatches in your avatar menu, next to Light / Dark / System.',
      'The choice is per-device, so you can run Sepia on your laptop and High contrast on the machine by the window.',
      'Every theme is checked against WCAG AAA for body text before it can ship — the contrast figure under each name is the measured ratio, not a claim. Sepia and Solarized are the gentlest for long reading; High contrast and Midnight are built for bright rooms, low vision, and OLED screens.',
      'Priority and status colours deliberately do not change between themes. Red still means urgent everywhere.',
      'Form fields and buttons now have a visible border in light mode. The old one was so faint it was effectively invisible.',
    ],
  },
  {
    date: '2026-08-17',
    category: 'fix',
    title: 'Important: replies could be silently discarded',
    body:
      'Clicking Send starts a 5-second window where you can undo. If you ' +
      'navigated away during those 5 seconds — clicking “Back to tickets”, ' +
      'or opening another ticket — the reply was thrown away and nothing told ' +
      'you. The ticket looked replied-to, so it would get resolved while the ' +
      'client was still waiting. Leaving the page now sends the reply rather ' +
      'than cancelling it.',
    details: [
      'If a flushed send fails, you get an error that stays on screen until you dismiss it, and the draft is kept on the ticket.',
      'Worth a quick look back at anything you replied to and resolved unusually fast — a small number of replies may never have gone out.',
    ],
  },
  {
    date: '2026-08-17',
    category: 'fix',
    title: 'Reports could show “0% SLA breach” when nothing had loaded',
    body:
      'If the report query failed, the page rendered zeros across every stat ' +
      'card — including a 0% SLA breach rate — which is indistinguishable ' +
      'from a genuinely clean week. It now shows an error with a Try again ' +
      'button instead of inventing numbers.',
    details: [
      'Reports load at most 2,000 tickets per window. Crossing that now shows a notice instead of quietly undercounting.',
      'An invalid custom date range no longer leaves the previous window’s charts on screen.',
    ],
  },
  {
    date: '2026-08-17',
    category: 'feature',
    title: 'Dark mode',
    body:
      'Light, Dark, or System — System follows whatever your computer is set ' +
      'to. Find it in the menu under your avatar, top right. It’s also on the ' +
      'sign-in page so you can set it before logging in. Your choice is ' +
      'remembered on that device.',
    details: [
      'Email bodies still show on a white card in dark mode. That’s deliberate — emails carry the sender’s own colours, written for a white background, so on a dark one about half the text would vanish. Every mail client does the same thing.',
      'Your signature preview shows on white for the same reason: it’s a preview of what the client actually receives.',
    ],
  },
  {
    date: '2026-08-17',
    category: 'feature',
    title: 'Undo send when composing a brand-new email',
    body:
      'Compose already existed but sent instantly — the one message with no ' +
      'safety net was the cold outbound email to a contact who has never ' +
      'written to us. It now has the same 5-second undo window as a reply, ' +
      'and the button reads “Send now” if you want it gone immediately.',
  },
  {
    date: '2026-08-17',
    category: 'fix',
    title: 'Templates no longer paste raw HTML into a new email',
    body:
      'Inserting a saved template while composing a new ticket showed the ' +
      'underlying markup — “<p>Hi there</p>” instead of formatted text — and ' +
      'sent it that way. Replies were unaffected; only Compose had the bug. ' +
      'Both now share one implementation so they can’t drift apart again.',
  },
  {
    date: '2026-08-17',
    category: 'improvement',
    title: 'Ticket list is easier to scan',
    body:
      '“Select all” used to be a small text link on the far right of the ' +
      'list header, nowhere near the checkboxes it controlled. It’s now a ' +
      'checkbox at the top of the checkbox column, and it shows a dash when ' +
      'only some rows are selected. The right-hand side of each row was also ' +
      'rebuilt into fixed columns, so everything lines up down the list ' +
      'instead of shifting from row to row.',
    details: [
      'Status labels were being cut off mid-word (“Waiting on clien”). Fixed everywhere in the app.',
      'Dates no longer wrap onto two or three lines — they show as 30m / 6h / 3d / 1 Aug, with the exact date and time on hover.',
      'The SLA chip now shows how late a ticket actually is (“4d over”, “25m left”) instead of a bare “SLA” flag.',
      'Status is now a coloured dot plus plain text rather than a filled pill, so the priority chip is the only strong colour on a row and genuinely stands out.',
      'Attachment, snooze and tone markers are one consistent muted icon set in their own column.',
      'Selecting rows then changing a filter clears the selection, so a bulk action can never hit rows you can no longer see.',
    ],
  },
  {
    date: '2026-08-17',
    category: 'improvement',
    title: 'Bulk actions ask before dismissing, and let you retry failures',
    body:
      'Resolve, Archive and Dismiss across more than 5 tickets now ask for ' +
      'confirmation first — previously one mis-click could archive 60 client ' +
      'conversations instantly. If some fail, those tickets stay selected so ' +
      'pressing the same button again retries exactly those.',
  },
  {
    date: '2026-08-17',
    category: 'improvement',
    title: 'Row actions work on a phone and on the keyboard',
    body:
      'Claim / Resolve / Escalate / Dismiss / Archive on a ticket row only ' +
      'appeared on mouse hover, so they were unreachable on a phone or ' +
      'tablet, and invisible to anyone tabbing through with a keyboard. ' +
      'They’re now a menu on touch screens and appear on keyboard focus.',
    details: [
      'A breached SLA is now a labelled “SLA” chip rather than a small red dot, and it no longer disappears when you hover the row.',
      'Screen readers announce which ticket each button acts on.',
    ],
  },
  {
    date: '2026-08-17',
    category: 'improvement',
    title: 'Error messages in plain English, and they stay on screen',
    body:
      'Failures used to flash a raw database error for 3.5 seconds — long ' +
      'enough to notice, not long enough to read. Errors now stay until you ' +
      'dismiss them and say what actually happened: a permissions problem, a ' +
      'connection drop, an expired session, or a paused automation.',
    details: [
      'Attachments that get skipped (over 10 files, or over 25MB each) now tell you instead of vanishing.',
      'Deleting a template, KB article or group now uses a proper dialog rather than the browser’s grey popup — which Chrome could suppress entirely, silently breaking those buttons.',
    ],
  },
  {
    date: '2026-08-17',
    category: 'fix',
    title: 'Automations: safety checks that could fail open',
    body:
      'Three problems in one area. The check that stops a rule acting twice ' +
      'on the same ticket ignored its own errors, so a failed lookup would ' +
      're-apply actions to tickets already processed. The banner could claim ' +
      '“Automations are ON” when it had failed to read the setting. And ' +
      '“Resume all” re-armed every destructive rule with a single unconfirmed ' +
      'click.',
  },
  {
    date: '2026-08-17',
    category: 'improvement',
    title: 'Readability, contrast and consistency across the app',
    body:
      'Secondary text was too light to meet accessibility standards in about ' +
      '68 places and has been darkened. Status and priority colours now come ' +
      'from one source, so “waiting on client” is the same colour everywhere ' +
      '— it used to be purple on a ticket row and amber on the contact panel.',
    details: [
      'The interface font (Inter) was specified but never actually loaded; it now does.',
      'Text sizes were collapsed from 15 different values down to a consistent scale.',
      'Added a skip-to-content link, and animations now respect your system’s “reduce motion” setting.',
      'If a page ever crashes, you now get a recovery screen with a Reload button instead of a blank white page.',
    ],
  },
  {
    date: '2026-07-06',
    category: 'fix',
    title: 'Saved views: “Save” no longer silently does nothing',
    body:
      'Naming a view used the browser’s native prompt popup — which browsers ' +
      'auto-suppress after being triggered a few times on the same page, so ' +
      'Save could quietly do nothing with no error at all. Save and Delete ' +
      'now use a proper in-app dialog instead, so this can’t happen.',
  },
  {
    date: '2026-07-06',
    category: 'fix',
    title: 'Saved dashboard views: clear button + save actually captures filters',
    body:
      'Two more fixes on top of the earlier "views actually apply" fix. ' +
      'Turns out "Save current view" wasn’t capturing your priority/status/' +
      'type/assignee chips at all — it was reading the browser URL, which ' +
      'those chips never touched, so most saved views ended up empty. Save ' +
      'now correctly snapshots whatever’s actually filtered. There’s also a ' +
      'new "Clear filters" option in the Views menu (only shows up when ' +
      'something’s actually filtered).',
    details: [
      'Any view you saved before today captured nothing — open it, set the filters again, and re-save with the same name to fix it.',
    ],
  },
  {
    date: '2026-07-06',
    category: 'fix',
    title: 'Saved dashboard views actually apply now',
    body:
      'Clicking a saved view in “Views” on the dashboard could silently do ' +
      'nothing, or land you on the wrong inbox. Both fixed: applying a view ' +
      'now always updates the filters immediately and stays in the inbox ' +
      'you’re currently looking at.',
  },
  {
    date: '2026-07-06',
    category: 'feature',
    title: 'Get emailed when someone adds you as a watcher',
    body:
      'Being added as a watcher on a ticket already showed up as an in-app ' +
      'notification — now it can email you too, same as replies and ' +
      'reassignments. It’s on by default; turn it off anytime in Settings → ' +
      'Email notifications → “Added as watcher”.',
  },
  {
    date: '2026-06-29',
    category: 'fix',
    title: 'Missing email attachments now self-heal',
    body:
      'Occasionally an inbound email arrived without its attachment (the mail ' +
      'provider didn’t hand it over on the first pass). The system now ' +
      're-fetches attachments straight from Gmail when that happens, so PDFs ' +
      'and other files reliably land on the ticket — across every inbox.',
  },
  {
    date: '2026-06-29',
    category: 'feature',
    title: 'All thread attachments in one place, grouped by type',
    body:
      'The ticket page now has an “Attachments” section in the right rail that ' +
      'gathers every file from across the whole conversation — grouped into ' +
      'Images, Documents, Spreadsheets, Archives, and Other — so you can grab an ' +
      'invoice or screenshot without scrolling the thread. Attachments still ' +
      'appear on each individual message too.',
  },
  {
    date: '2026-06-29',
    category: 'feature',
    title: 'Automations now run on new incoming email',
    body:
      'Enabled rules with scope “New tickets” or “Both” now fire automatically ' +
      'as email arrives — no more needing to click “Run on existing”. So a rule ' +
      'like “assign billing tickets to Kate” will catch new billing emails the ' +
      'moment they land. Actions are attributed to Automation and recorded in the ' +
      'activity log, same as before.',
    details: [
      'AI-judgement rules are evaluated per new ticket too (one AI check on tickets that pass the cheaper filters first).',
      'The global “Pause all” switch and per-rule enable still gate everything.',
    ],
  },
  {
    date: '2026-06-29',
    category: 'fix',
    title: 'Automation actions are attributed to “Automation”',
    body:
      'When a rule assigns, closes, or otherwise changes a ticket, the ticket’s ' +
      'activity now reads “Automation (rule name)” instead of crediting whichever ' +
      'admin happened to run the rule (e.g. it no longer says “Rex claimed this ' +
      'ticket” for an auto-assignment).',
  },
  {
    date: '2026-06-29',
    category: 'improvement',
    title: 'Richer automation activity log + working counts',
    body:
      'The activity log now shows each affected ticket by subject and sender, ' +
      'with an expandable row that previews what changed (before vs now) plus ' +
      'the AI’s reasoning — so you can review without leaving the page. Each ' +
      'row links straight to the ticket in a new tab, and the “applied ×” ' +
      'counter on each rule now updates correctly after a run.',
  },
  {
    date: '2026-06-29',
    category: 'improvement',
    title: 'Cleaner automation rule editor',
    body:
      'The rule builder is tidied up into clearer sections, and long AI ' +
      'instructions now wrap into a proper text box instead of scrolling off ' +
      'sideways. Condition and action fields use the full width so nothing ' +
      'gets cramped.',
  },
  {
    date: '2026-06-29',
    category: 'feature',
    title: 'Automations: AI judgement rules ("close anything marketing")',
    body:
      'A rule can now include an AI judgement written in plain English — ' +
      '“this email is marketing / promotional” — and AI decides it per ticket, ' +
      'with a one-line reason. Combine it with normal filters to keep it cheap. ' +
      'You review the AI’s picks (and its reasoning) before anything is applied, ' +
      'and every action is still fully revertable.',
    details: [
      'Add it via “Add AI judgement” in the rule editor; “Preview AI judgement” shows the verdicts before you save.',
      'Bulk runs classify candidates first and only act on the ones AI confirms; the reason is saved to the activity log.',
      'Cost guard: the editor warns when a rule would scan a lot of tickets and nudges you to add filters first.',
    ],
  },
  {
    date: '2026-06-29',
    category: 'feature',
    title: 'Automations: past-or-future scope, activity log + revert',
    body:
      'When building a rule you now choose whether it runs on new tickets ' +
      'only, existing tickets (a one-off pass), or both. Every action a rule ' +
      'takes is recorded in an activity log, and you can Revert a single ' +
      'action — or all of them — to restore tickets to exactly how they were ' +
      'before the rule ran. Use it if a rule does something you didn’t intend.',
    details: [
      '“Run on existing” now snapshots each ticket first, so reverting can reopen a dismissed ticket, restore its priority, assignee, or snooze.',
      'Bulk runs are idempotent (a rule won’t touch the same ticket twice) and capped per run.',
    ],
  },
  {
    date: '2026-06-29',
    category: 'fix',
    title: 'Bulk actions no longer knock out ticket actions',
    body:
      'Selecting a lot of tickets and bulk-closing (or claiming/resolving) ' +
      'used to fire every request at once, which could overwhelm the ' +
      'automation backend and disable it — after which every ticket action ' +
      'failed with a “404 / Active version not found” error. Bulk actions ' +
      'now run a few at a time so they can’t flood it.',
  },
  {
    date: '2026-06-29',
    category: 'feature',
    title: 'Automations — describe a rule, let AI build it (admins)',
    body:
      'Admins can now set up rules that act on tickets automatically. Open ' +
      'Admin → Automations, describe what you want in plain English (e.g. ' +
      '“dismiss everything from noreply@news.com” or “assign anything from ' +
      '@bigclient.com to Sarah and mark P2”), and AI drafts a structured ' +
      'rule you review before it runs. Every rule starts disabled, shows ' +
      'exactly how many existing tickets it matches, and asks for a typed ' +
      'confirmation before enabling anything destructive.',
    details: [
      'Actions: assign, dismiss, set priority, snooze, add watcher, skip Slack, add an internal note.',
      'Live preview shows the match count (and warns in red if a rule is too broad) before you save.',
      'A global “Pause all” kill switch instantly halts every automation.',
      'Run a rule on existing tickets in bulk — behind an exact count + typed “APPLY” confirmation, capped and audited.',
      'Rules never send email replies, and are admins-only end to end (enforced by the database, not just the UI).',
    ],
  },
  {
    date: '2026-06-22',
    category: 'improvement',
    title: 'Tidier composer toolbar on mobile + emoji shortcut',
    body:
      'On phones the formatting toolbar now shows just the common buttons ' +
      '(bold, italic, underline, link, emoji) with a “⋯ More” menu for the ' +
      'rest (lists, quote, code, indent, clear), so it no longer squishes ' +
      'every icon into a cramped bar. Send stays pinned on the right. You ' +
      'can also open the emoji picker with a keyboard shortcut: ' +
      '⌘/Ctrl + Shift + E.',
  },
  {
    date: '2026-06-22',
    category: 'fix',
    title: 'Links survive pasting, and the emoji picker works',
    body:
      'Pasting from Slack, Gmail, or another editor into a reply, compose, ' +
      'or template now keeps your links clickable (plus basic formatting ' +
      'like bold and lists) instead of flattening everything to plain text. ' +
      'And the emoji picker in the compose window is clickable again — it ' +
      'was opening but the emojis couldn’t be tapped.',
  },
  {
    date: '2026-06-22',
    category: 'improvement',
    title: 'Always up to date, and better on mobile',
    body:
      'The app now checks for new versions in the background and refreshes ' +
      'itself, so everyone stays on the latest release — no more stale ' +
      'screens or out-of-date behaviour from a cached version. On phones, ' +
      'you can now switch inboxes from the menu (tap the hamburger → Inbox), ' +
      'and the Inboxes settings page now lays out properly on small screens.',
    details: [
      'New deploys roll out to open tabs / installed apps within about a minute.',
      'Mobile: inbox switcher with new-ticket counts lives in the nav menu.',
      'Inboxes settings cards + dialogs are now mobile-friendly.',
    ],
  },
  {
    date: '2026-06-22',
    category: 'improvement',
    title: 'New-ticket counts on the inbox switcher',
    body:
      'The inbox switcher in the top bar now shows a badge with the number ' +
      'of new (unactioned) tickets in each inbox. The switcher button also ' +
      'shows a count of new tickets waiting in your other inboxes, so you ' +
      'can see at a glance where new work has landed without opening the ' +
      'menu. Counts refresh automatically.',
  },
  {
    date: '2026-06-22',
    category: 'fix',
    title: 'Compose now follows the inbox you’re in',
    body:
      'Composing a new ticket now respects the inbox you’re viewing: ' +
      'the email is sent from that inbox’s address, the ticket is filed ' +
      'under it, and it follows that inbox’s Slack setting — so ' +
      'composing from a Slack-off inbox (like Tristan’s) no longer posts ' +
      'to #tickets-new. Composing from a non-inbox page uses the primary ' +
      'support inbox. (Compose had also been failing outright since the ' +
      'multi-inbox rollout; that’s fixed too.)',
  },
  {
    date: '2026-06-22',
    category: 'improvement',
    title: 'Inbox switcher + colour-coding, and tighter inbox privacy',
    body:
      'The inbox switcher in the top bar now only lists inboxes you ' +
      'actually have access to, and each inbox can be given a colour ' +
      '(set it when you edit an inbox at /inboxes) that shows as a badge ' +
      'wherever tickets from different inboxes mix. We also closed a set ' +
      'of gaps where restricted-inbox tickets could surface outside the ' +
      'app — in the morning digest, SLA Slack alerts, the weekly report, ' +
      'journey exports, and notifications. Confidential inboxes are now ' +
      'kept out of all of those for anyone without access.',
    details: [
      'Resolved tickets from a Slack-off inbox no longer cross-post to #tickets-resolved.',
      'Morning digest only shows tickets from inboxes you can access.',
      'SLA breach alerts for confidential / Slack-off inboxes no longer post to the public Slack channel.',
      'The weekly report excludes confidential and Slack-off inbox tickets.',
      'Journey exports only include tickets the exporter has access to.',
    ],
  },
  {
    date: '2026-06-19',
    category: 'feature',
    title: 'Multi-inbox is live — Tristan Bond inbox onboarded end-to-end',
    body:
      'Adding a new inbox is now a three-click flow: create the inbox ' +
      'row at /inboxes, paste the OAuth token in the new Credentials ' +
      'dialog, then run one script command. Behind the scenes the ' +
      'script provisions n8n credentials, inserts a Gmail Trigger + ' +
      'tag node into workflow 01, and connects it to the existing ' +
      'pipeline. Replies from non-primary inboxes (like tristanbond@) ' +
      'send FROM the inbox address natively. Tristan Bond\'s inbox is ' +
      'live now — Faith + Rex have access, Slack notifications are ' +
      'off, OAuth is stored encrypted in Supabase Vault.',
  },
  {
    date: '2026-06-19',
    category: 'feature',
    title: 'Multi-inbox Phase C — token vault + multi-trigger architecture',
    body:
      'Foundation for adding more inboxes without cloning workflows. ' +
      'OAuth refresh tokens for each inbox now live in encrypted Supabase ' +
      'Vault storage — set + rotated via a new Credentials dialog on each ' +
      'inbox card at /inboxes. The SPA never reads tokens back; the ' +
      'dialog only shows whether one is set and when it was last synced ' +
      'to n8n. The inbound pipeline is refactored so one workflow can ' +
      'handle multiple Gmail Trigger nodes (one per inbox) instead of ' +
      'cloning N workflows — change ships dormant until trigger nodes ' +
      'are added.',
    details: [
      'Tristanbond@ inbox row + access grants are seeded for Faith + Rex.',
      'Each inbox card carries a state pill: "No OAuth" / "Needs n8n sync" / "Ready".',
    ],
  },
  {
    date: '2026-06-18',
    category: 'feature',
    title: 'Multi-inbox foundation — add more email addresses with per-user access',
    body:
      'Schema + admin UI for managing multiple support inboxes. Today the ' +
      'system runs on one address (support@); this lays the groundwork to ' +
      'add billing@, partnerships@, and a confidential / HR inbox without ' +
      'mixing queues. Admins manage who can see which inbox at /inboxes ' +
      '(under the Admin dropdown). New members get zero access by default ' +
      'on any inbox added after this — every grant is explicit. The ' +
      'confidential flag hides an inbox completely from non-granted users ' +
      'everywhere (dashboards, search, contacts, activity, reports, ' +
      'notifications, Slack). Nothing changes for the current support@ ' +
      'flow — every active member was backfilled with access so today ' +
      'reads identical to yesterday.',
    details: [
      'Activating a new inbox in n8n (pasting Google OAuth credentials) is a separate runbook step — the SPA never touches refresh tokens.',
      'Per-inbox Slack toggles let you silence a queue, or override the default channel (e.g. route the confidential inbox to a private channel).',
    ],
  },
  {
    date: '2026-06-17',
    category: 'improvement',
    title: 'Search pill in the top nav — jump to any ticket without the keyboard shortcut',
    body:
      'The Cmd+K command palette has always been there; it now also ' +
      'has a visible Search… pill in the nav bar next to Compose so ' +
      'you can click straight into it without knowing the shortcut. ' +
      'Same palette — searches tickets, contacts, KB articles, and ' +
      'pages. On narrow screens the pill collapses to a search icon.',
  },
  {
    date: '2026-06-17',
    category: 'improvement',
    title: 'End-of-day recap skips tickets you only claimed and didn\'t reply to',
    body:
      'Bulk-claiming or reassigning a batch of tickets used to inflate ' +
      'the recap with rows where the only thing that happened was the ' +
      'assignment column flipping. Those produced uninformative "Ticket ' +
      'X: assigned to you, no further action" entries. Now a ticket only ' +
      'shows up in the recap if you actually replied, resolved, ' +
      'escalated, changed status, or did anything other than just claim ' +
      'it. Claim + reply still counts — the reply is what pulls it in.',
  },
  {
    date: '2026-06-17',
    category: 'feature',
    title: 'Leave reminders ping admins 7 / 3 / 1 days before someone\'s away',
    body:
      'Every approved leave now triggers a Slack DM + in-app ' +
      'notification to every admin at the 7-day, 3-day, and 1-day ' +
      'marks before it begins (one ping per milestone). Gives the team ' +
      'time to redistribute work and confirm coverage before the day-of ' +
      'scramble. The scan is one cron run a day at 09:00 Sydney — ~30 ' +
      'n8n executions a month, well under the quota.',
  },
  {
    date: '2026-06-17',
    category: 'improvement',
    title: '"Close" button is now "Dismiss" — easier to tell apart from Resolve',
    body:
      'The Ban-icon button on tickets used to be labelled "Close", which ' +
      'people kept confusing with Resolve — clicking it on a real ticket ' +
      'archived the thread as marketing / spam. It\'s now labelled ' +
      '"Dismiss" so the difference is obvious at a glance. The tooltip ' +
      'spells out when to use each: Dismiss = marketing / spam / no ' +
      'action; Resolve = legitimate ticket you\'ve finished. The ' +
      'underlying behaviour and the dispatcher action are unchanged.',
  },
  {
    date: '2026-06-12',
    category: 'improvement',
    title: 'End-of-day recap excludes marketing / spam from the per-client breakdown',
    body:
      'Non-client emails (marketing pitches, recruiter spam, ' +
      'auto-replies, bounces, internal email loops — anything the ' +
      'triage AI tagged as "other") used to clutter up the per-' +
      'client section of the My Day recap. Now they\'re counted in ' +
      'the headline total, kept out of the prose blocks, and ' +
      'surfaced as a single line in the Trends section showing how ' +
      'many there were and the top senders.',
  },
  {
    date: '2026-06-12',
    category: 'fix',
    title: 'Date filter on dashboard sorts by the column you\'re filtering',
    body:
      'Filtering by "Last team reply · Today" was missing tickets you ' +
      'definitely replied to today, if the ticket itself was created ' +
      'months ago. The query always ordered by creation date and only ' +
      'looked at the 500 newest by creation, so older tickets with ' +
      'recent activity fell off. Now the list sorts by whichever date ' +
      'column you\'re filtering — so "Last team reply" actually shows ' +
      'the most-recently-replied tickets first.',
  },
  {
    date: '2026-06-12',
    category: 'fix',
    title: 'Clicking a notification for the ticket you\'re already on refreshes it',
    body:
      'If you were on a ticket page and a new client reply landed, ' +
      'clicking the notification did nothing visible — the URL was ' +
      'already the same so the page never re-fetched. The reply now ' +
      'appears straight away when you click the notification (from ' +
      'the bell dropdown or the full notifications page).',
  },
  {
    date: '2026-06-12',
    category: 'fix',
    title: 'Watched tickets that fell off the page now show again on My Day',
    body:
      'My Day\'s "Watching" section was intersecting watched IDs with ' +
      'the in-memory dashboard page, so any watched ticket older than ' +
      'the 500 most-recent silently disappeared. It also hid archived ' +
      'tickets even when they had fresh client activity. Now it ' +
      'fetches your watched tickets directly and shows every one ' +
      'with an active status, including archived-but-reopened. New ' +
      'client replies also un-archive the ticket automatically so ' +
      'they don\'t go invisible in the first place.',
  },
  {
    date: '2026-06-12',
    category: 'fix',
    title: 'Dashboard date filter now actually filters',
    body:
      'The Date chip on the dashboard ("Today", "Last 7 days", custom ' +
      'range, etc.) was setting state but never reaching the database — ' +
      'so clicking any preset silently did nothing. The filter now ' +
      'works against every date column ("Created", "Last team reply", ' +
      '"Resolved", etc.) and the list refetches the moment you tap a ' +
      'preset or apply a custom range.',
  },
  {
    date: '2026-06-12',
    category: 'fix',
    title: '"Last team reply" date filter now sees admin-UI replies live',
    body:
      'When you sent a reply from the admin composer, the ticket\'s ' +
      'last-team-reply timestamp wasn\'t updated until the Gmail webhook ' +
      'looped back (seconds to minutes later, sometimes never). So a ' +
      '"Last team reply today" filter could miss tickets you just ' +
      'replied to. The timestamp now updates the instant you click Send, ' +
      'and a one-time backfill heals the historical column from the ' +
      'message log.',
  },
  {
    date: '2026-06-08',
    category: 'improvement',
    title: 'All composer actions now live in the floating bar',
    body:
      'Draft, Polish, Templates, Attach, Save-as-template, and Send ' +
      'used to live in a separate row below the editor that scrolled ' +
      'with the page. They\'re now folded into the same floating ' +
      'island bar as the formatting glyphs so every reply action is ' +
      'one click away no matter how long the body grows. AI Draft / ' +
      'Polish / journey + day-summary prompts also updated to stop ' +
      'producing em-dashes (—), and any that slip through are ' +
      'replaced with commas at the response boundary.',
  },
  {
    date: '2026-06-08',
    category: 'improvement',
    title: 'Cursor turns into a pointer over every clickable thing',
    body:
      'Hovering buttons, tabs, dropdown items, switches, checkboxes — ' +
      'anything you can click in the app — now changes your cursor to ' +
      'the pointing-finger so you can tell at a glance what\'s ' +
      'actionable. Disabled buttons stay as the default arrow so the ' +
      'cursor never lies about whether something will respond.',
  },
  {
    date: '2026-06-08',
    category: 'improvement',
    title: 'Images now open in a lightbox instead of a new tab',
    body:
      'Click any image — an attachment in a ticket, an inline image ' +
      'in a client\'s email, a screenshot in a team note, a photo in ' +
      'a Customer Assist chat — and it now pops up in a centred ' +
      'fullscreen overlay instead of opening in a new browser tab. ' +
      'Close with Esc or click the dark backdrop; the overlay carries ' +
      'Download + Open-original buttons so the old behaviour is still ' +
      'one click away. PDFs and other files still download or open ' +
      'normally.',
  },
  {
    date: '2026-06-08',
    category: 'improvement',
    title: 'Template editor now has full formatting + emoji like the composer',
    body:
      'Creating or editing a saved reply template used to be a plain ' +
      'mono-font textarea — fine for plain-text, awkward for anything ' +
      'with bold / italic / lists / links. The body field now uses ' +
      'the same rich editor as the reply composer: bold, italic, ' +
      'underline, lists, links, blockquote, inline code, emoji — all ' +
      'one click away. Old plain-text templates auto-convert to ' +
      'paragraphs the first time you open them.',
  },
  {
    date: '2026-06-08',
    category: 'improvement',
    title: 'Floating formatting bar morphs into an island while scrolling',
    body:
      'The bold / italic / list / link / emoji bar in the reply ' +
      'composer now floats as a centred pill-shaped island while you ' +
      'scroll through a long reply, then smoothly snaps back to a ' +
      'normal full-width docked bar when you reach the end of the ' +
      'composer. Same buttons, always within reach, less visual ' +
      'noise in the middle of the body.',
  },
  {
    date: '2026-06-08',
    category: 'fix',
    title: 'Templates button in the composer opens the picker again',
    body:
      'The Templates button in the reply composer was opening its ' +
      'popover into invisibly-clipped space, so clicking it looked ' +
      'like nothing happened. The picker now renders correctly above ' +
      'the button — click it, filter by name, click a template to ' +
      'drop it in at your cursor.',
  },
  {
    date: '2026-06-08',
    category: 'fix',
    title: 'Ticket page right rail floats again as you scroll the thread',
    body:
      'The sidebar on the ticket page (Actions, Contact tickets, ' +
      'Activity, Notes) stopped sticking to the top as you scrolled ' +
      'through long threads — a side effect of an earlier mobile ' +
      'overflow fix. The rail now floats correctly again, and its ' +
      'inner scrollbar is hidden visually so the column looks clean ' +
      'whether or not the panel needs to scroll internally.',
  },
  {
    date: '2026-06-08',
    category: 'improvement',
    title: 'Mobile dashboard: assignee avatar now shows on each row',
    body:
      'The assignee photo is now visible on every ticket row on ' +
      'mobile, tucked into the right side of the status-pill row at ' +
      'the top of the card. You can tell who owns a ticket at a ' +
      'glance without opening it. Away/on-leave overlay + tooltip ' +
      'work the same as on desktop.',
  },
  {
    date: '2026-06-08',
    category: 'improvement',
    title: 'Mobile dashboard: status is now a coloured pill above the subject',
    body:
      'On the ticket list, statuses like "waiting on client" used to ' +
      'truncate to "waiting on cl…" because they had to share a row ' +
      'with the sender + timestamp. The status now sits on its own ' +
      'row above the subject as a coloured pill — full word visible, ' +
      'colour code still does the at-a-glance work (blue = new, ' +
      'purple = waiting, emerald = resolved). Desktop view is ' +
      'unchanged.',
  },
  {
    date: '2026-06-08',
    category: 'fix',
    title: 'Mobile dashboard: long client names + subjects now truncate cleanly',
    body:
      'On the ticket list, a long client email or a long subject line ' +
      'was pushing the time + status text off the right edge of the ' +
      'row (clipped by the page overflow guard, so invisible). ' +
      'The sender + subject now ellipsise properly so the metadata ' +
      'always stays visible inside the row.',
  },
  {
    date: '2026-06-08',
    category: 'feature',
    title: 'Profile picture on the My Day greeting card',
    body:
      'Your profile photo now sits on the personalised "Good morning" ' +
      'hero card at the top of My Day, with the daily scene emoji ' +
      '(sunrise / coffee / moon …) as a small corner badge. No photo? ' +
      'Your initials show in the same chip — never a broken image.',
  },
  {
    date: '2026-06-08',
    category: 'fix',
    title: 'Mobile: long emails no longer make the page scroll sideways',
    body:
      'A long client email address (or a wide HTML table inside a ' +
      'client reply) used to push the message bubble wider than the ' +
      'phone screen, so the whole page scrolled left-right. Two ' +
      'fixes together: the message bubble now clips its contents at ' +
      'the bubble edge, and the app layout has an overflow guard so ' +
      'nothing inside can ever drag the page horizontally. Sticky ' +
      'bars + vertical scroll still work normally.',
  },
  {
    date: '2026-06-08',
    category: 'fix',
    title: 'Emoji picker no longer gets cut off in the notes panel',
    body:
      'The smile-face picker in the team-notes composer was being ' +
      'clipped by the surrounding card so half the grid disappeared. ' +
      'The popover now renders in a portal that escapes any ' +
      'overflow boundary, auto-flips above/below the button to stay ' +
      'on screen, and stays pinned to the trigger when the page ' +
      'scrolls. Same picker, just no more chopped-off corners.',
  },
  {
    date: '2026-06-08',
    category: 'fix',
    title: 'Mobile: long client names no longer push buttons off-screen',
    body:
      'On phones, a long client/clinic name or assignee name on the ticket ' +
      'detail page used to push the priority + status pills (and on a few ' +
      'screens, the action buttons) off the right edge of the viewport, ' +
      'which made the page look broken. Sender + assignee chips now ' +
      'truncate cleanly with an ellipsis, the Notifications popover + ' +
      'the Assign dropdown stay inside the screen, the Compose dialog ' +
      'footer wraps instead of clipping, and the live-assist client page ' +
      'header keeps its lock icon visible no matter how long the agent ' +
      'name is. Long subjects already wrapped to two lines on the ' +
      'dashboard list — those keep working the same.',
  },
  {
    date: '2026-06-08',
    category: 'fix',
    title: 'Outbound replies no longer have giant gaps between paragraphs',
    body:
      'Replies sent from the admin UI were arriving with ~50px of ' +
      'blank space between every paragraph — caused by the editor\'s ' +
      'default behaviour of inserting empty <div><br></div> spacers ' +
      'when you pressed Enter twice. The recipient (Gmail/Outlook) ' +
      'AND our own thread bubble saw the same wide gaps. Now those ' +
      'empty rows are stripped at send time and on render of old ' +
      'stored replies, and content paragraphs use proper <p> margins ' +
      'instead. Reads tight and professional in every mail client.',
  },
  {
    date: '2026-06-08',
    category: 'improvement',
    title: 'Hover any "Xm ago" timestamp to see the full date + time',
    body:
      'Every "5m ago / 2h ago" timestamp across the app — dashboard ' +
      'ticket cards, notifications, notes, My Day, contacts, KB, ' +
      'templates — now reveals the precise local date and time when ' +
      'you hover. No need to open the ticket just to check whether ' +
      '"3h ago" was at 9am or 11am.',
  },
  {
    date: '2026-06-08',
    category: 'improvement',
    title: 'Past 24 hours, timestamps show the full date + time',
    body:
      '"3d ago" / "7d ago" was less useful than just seeing the ' +
      'actual day and time, so anything older than 24 hours now ' +
      'reads as e.g. "5 Jun 2026, 14:32" in your local timezone. ' +
      'Inside the 24-hour window the relative form ("5m ago", ' +
      '"2h ago") still does its job for at-a-glance recency.',
  },
  {
    date: '2026-06-08',
    category: 'improvement',
    title: 'Times on ticket pages show "X minutes ago" in your timezone',
    body:
      'Every timestamp on a ticket page — the "Opened …" line at ' +
      'the top, every message bubble in the thread — now reads as ' +
      '"5m ago", "2h ago", or "3d ago" so you can skim the activity ' +
      'at a glance. Hover any timestamp for the precise date and ' +
      'time, rendered in YOUR local timezone instead of the ' +
      'team-default Sydney clock. The display ticks live, so ' +
      '"just now" rolls into "1m ago" → "5m ago" without ' +
      'refreshing the page.',
  },
  {
    date: '2026-06-08',
    category: 'improvement',
    title: 'Formatting toolbar moved to the bottom of the composer',
    body:
      'The formatting bar (bold / italic / lists / link / emoji) on ' +
      'the reply composer and new-ticket dialog now sits just under ' +
      'the text editor instead of above it. Long replies push your ' +
      'cursor toward the bottom of the box — the toolbar is now ' +
      'within easy reach there, no scrolling back up to bold a word.',
  },
  {
    date: '2026-06-08',
    category: 'improvement',
    title: 'Emoji picker on every composer',
    body:
      'The smile-face button is now in every place you can type a ' +
      'message — reply composer, new-ticket dialog, team notes — ' +
      'not just live assist. Same ~80-emoji popover, same one-click ' +
      'insert at the cursor. For the reply + new-ticket composers ' +
      'it sits at the right end of the formatting toolbar, for ' +
      'team notes it lives next to the paperclip in the action row.',
  },
  {
    date: '2026-06-03',
    category: 'fix',
    title: 'Shortcut tooltips no longer hide behind cards',
    body:
      'The keyboard-shortcut tooltips on the ticket-page action ' +
      'buttons (Assign, Resolve, Send, AI Draft, Polish) were ' +
      'being clipped by their surrounding cards and sticky bars — ' +
      'so the hint you needed to learn the shortcut was the one ' +
      'you couldn\'t see. Tooltips now render in a portal that ' +
      'escapes any overflow boundary, so the full label + key ' +
      'chips show no matter how nested the button is.',
  },
  {
    date: '2026-05-29',
    category: 'feature',
    title: 'My Day — generate a presenter-ready end-of-day recap',
    body:
      'A new "End-of-day recap" card on My Day. Pick Today, ' +
      'Yesterday, Last 7 days, Last 30 days, or a Custom range — ' +
      'Claude reads every ticket you touched in that window and ' +
      'writes a 2-4 paragraph summary: opening line about the shape ' +
      'of your day, then one em-dash entry per ticket with the ' +
      'client, the issue, and what you actually did. Copy button ' +
      'puts it on your clipboard ready for a standup or a Slack ' +
      'message to your team leader.',
  },
  {
    date: '2026-05-29',
    category: 'feature',
    title: 'AI-drafted follow-up email after live assist sessions',
    body:
      'When a Customer Assist session ends, the "AI draft follow-up" ' +
      'button on the toast asks Claude to read the chat transcript + ' +
      'write a concise recap email. The result lands in the Reply ' +
      'Composer for you to review and edit before sending — same ' +
      'human-in-the-loop guardrail as the existing Draft button. ' +
      'A static "Use template" fallback is still there for sessions ' +
      'where you want a quick generic placeholder instead.',
  },
  {
    date: '2026-05-29',
    category: 'improvement',
    title: 'Live assist polish — emoji, paste screenshots, chat-app chime, and a new Transcripts tab',
    body:
      'Four tweaks to the Customer Assist surface: (1) emoji picker ' +
      'in both agent + client compose — small popover, ~80 common ' +
      'emojis, no library bloat. (2) Paste screenshots straight into ' +
      'chat with Cmd-V — anonymous "image.png" clipboard pastes ' +
      'now get a friendly auto-name + queue as an attachment. ' +
      '(3) Chat-app chime fires on every new incoming message you ' +
      'can\'t see (tab hidden or widget minimized), alongside the ' +
      'existing OS banner. (4) Live assist transcripts now live on ' +
      'their own tab in the Notes panel so they don\'t drown the team ' +
      'conversation — Notes (your team\'s actual commentary) is the ' +
      'default; Live assist transcripts (auto-generated when a ' +
      'session ends) is one click away.',
  },
  {
    date: '2026-05-28',
    category: 'feature',
    title: 'Live-assist follow-up sequence — gentle nudges to boost show-up rate',
    body:
      'When you start a Customer Assist session and the client doesn’t ' +
      'join straight away, we now send up to two friendly reminders ' +
      'before the link expires. One at ~10 min in (~20 min left), one ' +
      'at ~20 min in (~10 min left). Both reminders re-send the magic ' +
      'link with a fresh “⏰ Valid until” chip. If the client has ' +
      'already joined we never send a reminder — that check runs ' +
      'twice (in the DB query and again before each send) so a ' +
      'mid-session ping is impossible. The existing 30-min "we missed ' +
      'you" courtesy email still fires for sessions that fully time out.',
  },
  {
    date: '2026-05-28',
    category: 'fix',
    title: 'Mobile fixes — composer toolbar, sticky bar, message headers',
    body:
      'On phone-sized screens the new Templates button (added to the ' +
      'composer toolbar this week) was pushing Send off the right ' +
      'edge of the screen. Three fixes: (1) toolbar buttons now ' +
      'render icon-only on mobile and show their labels from ' +
      'tablet-width up; (2) the sticky ticket header drops the ' +
      'priority badge + Resolve text label on mobile so the action ' +
      'cluster fits; (3) long to/cc lines in message headers now ' +
      'wrap instead of forcing the bubble wider than the screen.',
  },
  {
    date: '2026-05-27',
    category: 'fix',
    title: 'Notifications now appear instantly — no more refresh needed',
    body:
      'The bell + desktop banners were waiting on a 60-second poll, ' +
      'so a fresh notification could sit unseen for almost a minute. ' +
      'We now subscribe to a live Realtime channel on the ' +
      'notifications table, so a new row lights up the bell within ' +
      'a couple of hundred milliseconds and the desktop banner fires ' +
      'right behind it. A 25-second poll still runs as a safety net ' +
      'in case the WebSocket drops silently on a flaky network.',
  },
  {
    date: '2026-05-27',
    category: 'improvement',
    title: 'Unread count now shows in the tab title + a red dot on the favicon',
    body:
      'When you have unread notifications, the browser tab now reads ' +
      '"(3) PA Support Tickets" with a red dot on the icon — so you ' +
      'can spot activity at a glance even when the admin tab is ' +
      'buried behind Gmail and Slack. Both clear automatically when ' +
      'the bell unread count drops to zero. Pairs nicely with the ' +
      'desktop banners + chime added today.',
  },
  {
    date: '2026-05-27',
    category: 'feature',
    title: 'Desktop notifications + chime — get pinged even when the tab is in the background',
    body:
      'Settings → Desktop notifications now offers a banner + soft ' +
      'chime when a new notification arrives while the admin tab is ' +
      'not focused. Allow the browser permission, flip the toggle, ' +
      'and hit Test to verify your OS isn’t silencing it. The chime ' +
      'is two short tones (~200ms) and is independently toggleable.',
  },
  {
    date: '2026-05-27',
    category: 'fix',
    title: 'Sent replies now keep their formatting in the thread',
    body:
      'Bold, italics, bullet points, headings, blockquotes — they ' +
      'all now render in the ticket thread the same way you typed ' +
      'them in the composer. The previous build was applying ' +
      'Tailwind preflight resets to the message bubbles which ' +
      'silently stripped the bullets, paragraph spacing, and list ' +
      'indents. The recipient already saw the formatting fine over ' +
      'email — this was just an admin-UI display bug. We also ' +
      'cleaned up the body stored in the database so the thread ' +
      'doesn’t repeat the company logo + signature inside every bubble.',
  },
  {
    date: '2026-05-27',
    category: 'fix',
    title: 'Templates now insert with proper formatting — and a new button to insert anywhere',
    body:
      'Two fixes for the template flow: (1) inserting a template no ' +
      'longer shows the raw HTML tags — bold, paragraphs, line breaks ' +
      'all render correctly now. (2) A new "Templates" button in the ' +
      'composer toolbar opens a filter-as-you-type picker, so you ' +
      'can insert a template even mid-sentence (the / shortcut still ' +
      'works on a fresh line). Variables (' +
      '{{first_name}} etc) still fill in on insert in both modes.',
  },
  {
    date: '2026-05-27',
    category: 'improvement',
    title: 'Easier to scan a long thread — and the "on leave" pill follows you down',
    body:
      'Each message in a ticket thread now wears a chunky coloured ' +
      'left edge — blue for the client, emerald for the team — so you ' +
      'can scan a long conversation without reading the avatar first. ' +
      'Separately, when the ticket assignee is on approved leave, the ' +
      'sticky ticket header now carries a small amber "On leave · back ' +
      'Fri 23 May" pill — so you don’t have to scroll up to the full ' +
      'banner to remember they’re away.',
  },
  {
    date: '2026-05-27',
    category: 'improvement',
    title: 'The app opens instantly on return visits',
    body:
      'The "Checking sign-in…" splash that flashed every time you ' +
      'reloaded is gone for return visits. We now cache your validated ' +
      'team-member row in your browser, so on second-and-later loads ' +
      'the app paints immediately while the session re-validates ' +
      'silently in the background. First-ever sign-in is unchanged. ' +
      'If your access is revoked while you’re away, the background ' +
      're-check still bounces you to /login — nothing about security ' +
      'changes.',
  },
  {
    date: '2026-05-27',
    category: 'improvement',
    title: 'Hover any shortcut-enabled button to learn its key',
    body:
      'Send, AI Draft, Polish, Assign, and Mark Resolved now show a ' +
      'small tooltip with their keyboard shortcut on hover or keyboard ' +
      'focus — so the next time you reach for Send, your hand learns ' +
      '⌘↵ instead. Hit ? anywhere to see the full list.',
  },
  {
    date: '2026-05-27',
    category: 'feature',
    title: 'A "Covering for X" section on My Day so you know what you’re on the hook for',
    body:
      'When a teammate is on approved leave and you’re named as their ' +
      'backup, their open + actionable tickets now show up as their own ' +
      'amber section on your My Day — right under Needs Attention. No ' +
      'more piecing it together from notifications. Their parked ' +
      '(waiting-on-client) and snoozed tickets are excluded because ' +
      'those aren’t your problem; only the things that need a human to ' +
      'move them forward show up. If their queue happens to be empty ' +
      'right now, the section still appears with an "all clear" line so ' +
      'you have certainty it’s working.',
  },
  {
    date: '2026-05-27',
    category: 'improvement',
    title: 'Admin pages match the rest of the app',
    body:
      'Team, Contacts, Knowledge Base, Templates, and Journeys used to ' +
      'render rows as heavy outlined cards while everything else used ' +
      'soft, low-key panels — a small inconsistency that made those ' +
      'pages feel "older". They all share the same light surface ' +
      'language now: softer borders, no shadows, a subtle hover, and ' +
      'shaped loading skeletons + iconographic empty states matched to ' +
      'each surface.',
  },
  {
    date: '2026-05-27',
    category: 'improvement',
    title: 'Smoother loading + a softer feel between pages',
    body:
      'The app now uses shaped skeleton placeholders that match the ' +
      'exact silhouettes of tickets, messages, notes, and activity ' +
      'rows — so when a page is loading, it looks like the content ' +
      'is already there rather than a blank flash. Navigation between ' +
      'pages also gets a subtle 150ms fade so the app feels like one ' +
      'continuous surface rather than a series of hard cuts. Empty ' +
      'states (no notes yet, no activity yet, etc.) now share the ' +
      'same iconographic card treatment for visual consistency.',
  },
  {
    date: '2026-05-25',
    category: 'feature',
    title: 'Save replies as templates — shared with the team or private to you',
    body:
      'A new bookmark icon in the composer turns whatever you\'re ' +
      'currently writing into a reusable template. Pick "Shared" so ' +
      'the whole team can use + edit it, or "Private" so it stays in ' +
      'your own slash menu. Existing templates default to Shared (no ' +
      'behaviour change). The Templates page now has a filter tab and ' +
      'shows who created each template; the slash menu marks private ' +
      'ones with a small Private tag.',
  },
  {
    date: '2026-05-25',
    category: 'improvement',
    title: 'Live assist: 30-min countdown + we email the client if they no-show',
    body:
      'The Customer Assist widget now shows a live countdown to the ' +
      '30-minute auto-expire — neutral most of the time, amber in the ' +
      'last 5 minutes. When it hits zero AND the client never joined, ' +
      'we automatically email them a friendly "let\'s reschedule" note ' +
      'so they\'re not left wondering what happened. No action needed ' +
      'from you when this happens; the widget just shows "Session ' +
      'timed out" and you can dismiss it.',
  },
  {
    date: '2026-05-25',
    category: 'improvement',
    title: 'Replying claims the ticket for you',
    body:
      'If you reply to an unassigned ticket from the composer, the ' +
      'ticket auto-claims to you — no separate Claim click needed. ' +
      'Manual claim still works the same; this just covers the common ' +
      'case where you jump in to help on something nobody owns yet. ' +
      'Tickets that are already assigned to someone else are untouched.',
  },
  {
    date: '2026-05-25',
    category: 'improvement',
    title: 'Customer Assist: persistent floating widget + agent name',
    body:
      'The live chat is now a floating widget in the bottom-right corner ' +
      'that follows you anywhere in the app. Navigate between tickets, ' +
      'dashboard, settings — the session keeps running, Realtime stays ' +
      'connected, desktop notifications keep firing. Click the minimize ' +
      'button to collapse it to a slim pill while you work. Plus: the ' +
      'magic-link email and the client\'s join screen now use your actual ' +
      'name ("Liz from Practice Acceleration would like to chat") so ' +
      'clients know who they\'re talking to before they click.',
  },
  {
    date: '2026-05-25',
    category: 'improvement',
    title: 'Customer Assist: AFK alerts so you never miss a client',
    body:
      'Big green banner drops in the moment the client joins. Browser ' +
      'tab title flashes "(N) ..." when there are unread messages while ' +
      'the tab isn\'t focused. Plus opt-in desktop notifications: click ' +
      'Enable in the dialog\'s amber prompt the first time, and the OS ' +
      'will ping you when the client joins or sends a message even if ' +
      'this tab is hidden. Same protection works on the client side ' +
      '— they get notified when you join too.',
  },
  {
    date: '2026-05-25',
    category: 'improvement',
    title: 'Customer Assist: bigger chat window, attachments, no more lost messages',
    body:
      'Three big improvements to live sessions, based on real-world testing: ' +
      '(1) The dialog is now much larger with the chat as the main surface, ' +
      'so things stop getting cut off. ' +
      '(2) Messages no longer get lost if you send something before the ' +
      'client has joined — chat is persisted to the database and the client ' +
      'hydrates their history on join. Sensitive 2FA codes are still ' +
      'broadcast-only and never written to disk. ' +
      '(3) Both sides can now attach images and files (up to 10 MB each, ' +
      '3 per message). Images render inline.',
  },
  {
    date: '2026-05-25',
    category: 'feature',
    title: 'Customer Assist — go live with a client when async is too slow',
    body:
      'Need a 2FA code that\'ll expire before the client emails it back? ' +
      'Need to confirm a billing detail right now? Click "Live assist" on ' +
      'any ticket — we\'ll email the client a one-click link that opens a ' +
      'real-time chat with you in the same browser. When you need a code, ' +
      'click "Request 2FA code" and the client gets a masked input; the ' +
      'value lands in a banner on your screen for 60 seconds, then ' +
      'auto-vanishes. The code never touches the database. When you end ' +
      'the session, a transcript (with codes redacted) is saved as an ' +
      'internal note on the ticket.',
  },
  {
    date: '2026-05-25',
    category: 'feature',
    title: 'Leave requests with admin approval',
    body:
      'Schedule leave through a proper approval flow. On Settings → ' +
      'Leave requests, pick your dates, leave type (annual / sick / ' +
      'personal / other), and a backup. Admins review on the new ' +
      'Leave page and approve or reject — only approved leave triggers ' +
      'backup coverage and "on leave" badges. Admins get a Slack DM ' +
      'when a request comes in; you get an in-app ping when they ' +
      'decide; the whole team sees a heads-up when leave is approved.',
  },
  {
    date: '2026-05-25',
    category: 'feature',
    title: 'Company logo — brand the ticket system',
    body:
      'Admins can now upload a company logo on the new Company page. ' +
      'It shows in the top nav, on the sign-in page, in the header of ' +
      'every outbound email reply, and on every client-journey export. ' +
      'Drag and drop a PNG, JPG, SVG, or WebP (up to 2 MB). To upload, ' +
      'you need admin access — promote teammates from the Team page.',
  },
  {
    date: '2026-05-25',
    category: 'improvement',
    title: 'New home: tickets.patientsondemand.com',
    body:
      'The admin app now lives at its own address — ' +
      'tickets.patientsondemand.com. Update your bookmark; the old ' +
      'pa-admin-ui.pages.dev link still works, so nothing breaks if ' +
      'you forget. Sign-in, replies, and ticket links all use the new ' +
      'domain going forward.',
  },
  {
    date: '2026-05-25',
    category: 'improvement',
    title: 'Mentions highlight as you type a note',
    body:
      'When you @mention a teammate in an internal note, their name ' +
      'now lights up right in the input box as you type — Slack-style ' +
      '— so you can see at a glance who you\'ve tagged before saving.',
  },
  {
    date: '2026-05-25',
    category: 'improvement',
    title: 'Ticket status stays visible while you scroll',
    body:
      'On a long thread you no longer have to scroll back to the top ' +
      'to check a ticket\'s status. Once you scroll past the header, a ' +
      'slim bar slides in under the nav showing the subject, priority, ' +
      'status, and live SLA — plus quick Resolve and Assign buttons so ' +
      'you can act without scrolling.',
  },
  {
    date: '2026-05-19',
    category: 'feature',
    title: '"Welcome back" recap when you return from leave',
    body:
      'Your first day back, My Day greets you with a recap of what ' +
      'happened while you were away. Two parts: what happened on YOUR ' +
      'tickets (who covered, a tally, and the activity list), plus an ' +
      '"Across the team" section showing what everyone got done — ' +
      'overall counts and a per-teammate breakdown of resolves, ' +
      'replies, and internal notes. Dismiss it once you\'re caught ' +
      'up; shows for up to two weeks after you return.',
  },
  {
    date: '2026-05-18',
    category: 'improvement',
    title: '"On leave" label shows on tickets while someone\'s away',
    body:
      'When the assignee of a ticket is currently inside one of their ' +
      'away windows, the ticket detail page shows a clear amber ' +
      'banner — "Rex is on leave until Fri, 23 May 5:00 PM · Covered ' +
      'by Liz" — and every ticket row gets a small amber dot on the ' +
      'assignee\'s avatar with the same info on hover. So you know at ' +
      'a glance who to ping instead, without checking Settings.',
  },
  {
    date: '2026-05-18',
    category: 'feature',
    title: 'Away coverage — nominate a backup while you\'re off',
    body:
      'Going on leave? Head to Settings → Away coverage, set a date ' +
      'range, and pick a teammate to cover for you. While the window ' +
      'is active they\'ll get a copy of every notification you would ' +
      'have received (in-app + email), with a "(covering for you)" ' +
      'tag so they know it\'s a stand-in. You can schedule multiple ' +
      'windows in advance. Your assigned tickets stay yours — this is ' +
      'purely a notification safety net.',
  },
  {
    date: '2026-05-18',
    category: 'improvement',
    title: 'Attachments now stand out under messages',
    body:
      'The attachment row under each message used to blend into the ' +
      'body and was easy to miss. It\'s now a tinted card with an ' +
      '"Attachments (N)" header, a colour-coded file-type chip per ' +
      'row (CSV is green, PDF is red, archives are amber, etc.), the ' +
      'filename in bold, the file size + type, and a download icon. ' +
      'Image attachments still show as thumbnails inside the same ' +
      'card.',
  },
  {
    date: '2026-05-18',
    category: 'improvement',
    title: 'Paperclip icon on every ticket list',
    body:
      'The small paperclip glyph we just added to contact-page rows ' +
      'now also shows up on the Tickets dashboard, every section of ' +
      'My Day, and the "From this contact" mini list on ticket ' +
      'detail — so "this ticket has a file" is visible anywhere ' +
      'tickets are listed, no need to open each one to find out.',
  },
  {
    date: '2026-05-18',
    category: 'improvement',
    title: 'Contact page rows now show who owns it + if it has files',
    body:
      'The Tickets & activity rows on the contact page now display ' +
      'the current assignee\'s avatar and a paperclip icon when the ' +
      'ticket carries any attachments — so you can spot ownership and ' +
      '"there\'s a file in here" without expanding the row.',
  },
  {
    date: '2026-05-18',
    category: 'feature',
    title: 'New "Activity" page — see what the whole team is doing',
    body:
      'A new Activity tab shows every action across every ticket in ' +
      'one chronological feed: who claimed what, who replied, ' +
      'escalations, SLA breaches, follow-ups, the lot. Filter by ' +
      'event type, by actor, or by date range. Grouped by day for ' +
      'easy scanning. Load more to scroll back into history.',
  },
  {
    date: '2026-05-18',
    category: 'feature',
    title: 'Assign button now transfers + delegates',
    body:
      'The Claim and Reassign buttons merged into a single Assign ' +
      'picker — same shape as the new Watch picker. Self pinned at the ' +
      'top for one-click claim, every teammate listed below for one-' +
      'click reassign. Trigger label shows current state at a glance ' +
      '("Mine", "Assign", or the current owner\'s name). Reassigning ' +
      'still notifies the new assignee (as it always did).',
  },
  {
    date: '2026-05-18',
    category: 'improvement',
    title: 'Get pinged when someone adds you as a watcher',
    body:
      'When a teammate subscribes you to a ticket, you now get a ' +
      'notification ("Liz added you as a watcher · {subject}") so it ' +
      'doesn\'t silently appear in your Watching list. Self-watches ' +
      'and un-watches don\'t notify — no spam.',
  },
  {
    date: '2026-05-17',
    category: 'feature',
    title: 'Loop teammates in as watchers',
    body:
      'The Watch button on the ticket sidebar is now a picker — you ' +
      'can subscribe yourself with one click (Self pinned at the top) ' +
      'OR add any teammate as a watcher from the same menu. Great for ' +
      '"Liz, you should keep an eye on this" without needing them to ' +
      'open the ticket themselves. Search box appears once the list ' +
      'gets long.',
  },
  {
    date: '2026-05-17',
    category: 'fix',
    title: 'Polish handles Claude overload gracefully',
    body:
      'Occasionally Claude\'s API briefly runs out of capacity ' +
      '(HTTP 529 "Overloaded") and Polish would fail with a scary ' +
      'JSON error. It now quietly retries up to 3 times with ' +
      'backoff — most users will never see the error again. If it ' +
      'still can\'t reach Claude, you\'ll see a friendly "give it a ' +
      'few seconds and try again" message instead of raw JSON.',
  },
  {
    date: '2026-05-17',
    category: 'improvement',
    title: 'Watched tickets close the loop when resolved',
    body:
      'When a ticket you\'re watching gets resolved, you now get a ' +
      'notification and it automatically drops off your Watching ' +
      'list — no more stale tickets you forgot you were watching. ' +
      'If it ever gets reopened, it comes back.',
  },
  {
    date: '2026-05-17',
    category: 'feature',
    title: 'See the tickets you\'re watching',
    body:
      'Tickets you\'ve subscribed to now have their own home: a ' +
      '"Watching" section on My Day, and a "Watching" tab on the ' +
      'Tickets page (next to Mine / Team / Unassigned). No more ' +
      'losing track of a ticket you wanted to keep an eye on but ' +
      'aren\'t assigned to.',
  },
  {
    date: '2026-05-17',
    category: 'improvement',
    title: 'My Day gets a fresh daily scene',
    body:
      'My Day now opens with a calm scenery photo that changes every ' +
      'day — mountains, forests, coastlines — with a greeting that ' +
      'knows the time, a line that reacts to how your week is going, ' +
      'and a motivating quote. A red dot sits on the My Day tab until ' +
      'you check in for the day, so it\'s worth a look each morning.',
  },
  {
    date: '2026-05-17',
    category: 'feature',
    title: 'New "My Day" home page',
    body:
      'Signing in now lands you on My Day — a page personalised to ' +
      'you. It shows your SLA-at-risk tickets up top, then your ' +
      'queue (priority first), what\'s waiting on clients, your ' +
      'snoozed tickets about to wake, unassigned tickets up for ' +
      'grabs, and your unread notifications — all in one place, no ' +
      'filters to set. The full Tickets dashboard is unchanged and ' +
      'still one click away.',
    details: [
      'Live in-app version of the morning digest email',
      'Scoped to you — every teammate sees their own',
      'Stat strip: open · SLA at risk · waiting · resolved this week',
    ],
  },
  {
    date: '2026-05-16',
    category: 'feature',
    title: 'Contact edit history',
    body:
      'Every contact now has an "Edit history" panel showing who ' +
      'changed what and when — name, relationship, tier, and note ' +
      'changes are each logged with the exact before → after. It\'s ' +
      'append-only and captured automatically server-side, so it\'s ' +
      'a reliable audit trail.',
  },
  {
    date: '2026-05-16',
    category: 'feature',
    title: 'Edit contacts',
    body:
      'Open any contact and hit "Edit contact" to fix their display ' +
      'name, set Client vs Prospect, choose a tier, and add an ' +
      'internal team-only note. Your edits are protected — Patients ' +
      'on Demand will no longer overwrite them on the next email ' +
      '(the contact shows an "Edited · POD sync off" badge so it\'s ' +
      'clear). Client status and tier feed triage + SLA targets, so ' +
      'correcting them actually changes how future tickets are handled.',
  },
  {
    date: '2026-05-16',
    category: 'fix',
    title: 'Profile pictures now show everywhere (and on the Team page)',
    body:
      'Some teammates showed only initials because their Google ' +
      'profile photo URL was being blocked when loaded from the app. ' +
      'Those now load reliably, and if any avatar ever fails it falls ' +
      'back cleanly to initials instead of a broken image. The Team ' +
      'page now shows everyone\'s photo too, with inactive members ' +
      'dimmed.',
  },
  {
    date: '2026-05-15',
    category: 'feature',
    title: 'Filter the dashboard by date — and pick which date',
    body:
      'A new "Date" chip on the filter bar lets you scope the ticket ' +
      'list by Created, First reply, Resolved, Last team reply, ' +
      'Last client reply, or Any update — paired with preset ranges ' +
      '(Today, 7 / 30 / 90 days, This month, Last month, This quarter) ' +
      'or a custom From / To. Switch the field with an active range ' +
      'to flip between "created this week" and "resolved this week" ' +
      'in one click.',
  },
  {
    date: '2026-05-15',
    category: 'feature',
    title: 'Snooze that actually snoozes',
    body:
      'Snooze used to be a glorified post-it — now it really hides ' +
      'the ticket from your dashboard, pauses its SLA alerts, and ' +
      'auto-wakes it when the timer expires. Five presets (1h, 4h, ' +
      'Tomorrow 9am, 2 days, Next Monday 9am) plus an exact-time ' +
      'picker. When a ticket is snoozed the Snooze button morphs ' +
      'into a Wake up button so you can bring it back early. Snoozed ' +
      'tickets live under their own Snoozed tab on the dashboard, ' +
      'and you\'ll see an indigo banner at the top of the ticket ' +
      'telling you exactly when it returns.',
    details: [
      'SLA monitor skips snoozed tickets — no false breach alerts',
      'Auto-resurface runs every 5 minutes in the background',
      'Activity timeline records both the snooze and the wake-up',
    ],
  },
  {
    date: '2026-05-14',
    category: 'feature',
    title: 'Contact page: per-ticket accordion with timeline view',
    body:
      'Every ticket from a contact now sits in its own accordion ' +
      'row — click the chevron to reveal a vertical timeline of ' +
      'what happened on it (created, triaged, claimed, replied, ' +
      'resolved, SLA breaches, follow-ups). Click the subject ' +
      'or the open-arrow to jump into the full ticket. The most ' +
      'recent ticket is expanded by default so the page lands on ' +
      'useful content. Replaces the flat all-events log so each ' +
      'event is unambiguously tied to its ticket.',
  },
  {
    date: '2026-05-14',
    category: 'improvement',
    title: 'More contact metrics + correct assignee avatars',
    body:
      'The Health snapshot now shows First seen, Last contact, ' +
      'Avg first reply, Top category, and Most-handled-by (with ' +
      'the right profile picture). And the assignee chips on each ' +
      'ticket row now show real profile pictures instead of ' +
      'silently falling back to initials.',
  },
  {
    date: '2026-05-14',
    category: 'feature',
    title: 'See this contact\'s other tickets right from the ticket view',
    body:
      'New "From this contact" panel in the ticket detail right rail. ' +
      'Lists every other ticket from the same email address with ' +
      'status, priority, and how long ago they were opened or ' +
      'resolved — one click jumps to any of them. No more switching ' +
      'to the Contacts page to check whether someone is a repeat ' +
      'caller or how their last issue ended.',
  },
  {
    date: '2026-05-14',
    category: 'improvement',
    title: 'Cursor lands in the reply box when you open a ticket',
    body:
      'Opening a ticket on desktop now drops the cursor straight into ' +
      'the reply editor and scrolls the composer into view — no more ' +
      'scrolling down to find it. Mobile/tablet are exempt so the ' +
      'on-screen keyboard doesn\'t pop up the moment you tap a ticket.',
  },
  {
    date: '2026-05-13',
    category: 'fix',
    title: 'Reply composer now scrolls with the page',
    body:
      'The composer used to be pinned to the bottom of the viewport, ' +
      'so a pasted image or long draft would grow it upwards and ' +
      'cover the thread you were reading. Now it sits at the natural ' +
      'end of the thread and scrolls with the page — exactly like ' +
      'Gmail. Paste a huge screenshot, the page just gets longer; ' +
      'scroll up to re-read any part of the conversation.',
  },
  {
    date: '2026-05-13',
    category: 'fix',
    title: '"Open in Gmail" buttons now open the support inbox',
    body:
      'Slack action buttons and the Gmail pill on tickets used the position-' +
      'based account selector (`u/0`) — so if your support inbox wasn\'t the ' +
      'first Gmail account in your browser, the link opened the wrong inbox. ' +
      'Now they pass the support address via Gmail\'s `authuser=` parameter so ' +
      'Gmail picks the right account regardless of position.',
  },
  {
    date: '2026-05-13',
    category: 'feature',
    title: 'Pull-to-refresh on mobile + PWA',
    body:
      'Swipe down at the top of any page to refresh. The whole React Query ' +
      'cache invalidates — tickets, messages, notifications, contacts — ' +
      'and active queries refetch immediately. Faster and smoother than ' +
      'a full page reload, and your dialogs / composer drafts survive.',
  },
  {
    date: '2026-05-13',
    category: 'fix',
    title: 'Missed attachments now recoverable in one click',
    body:
      'When an inbound email\'s attachment fails to download, the message ' +
      'shows an amber "couldn\'t download" chip with a Retry button. One ' +
      'click re-fetches it from Gmail and adds it to the ticket. Backfilled ' +
      'all 17 attachments that were silently lost since 8 May (Storage auth ' +
      'header regression).',
  },
  {
    date: '2026-05-12',
    category: 'feature',
    title: 'AI thread summary tab',
    body:
      'Long ticket threads now have a Summary tab next to Thread / KB Matches. ' +
      'One click → Claude Haiku writes a 5-part recap so you can catch up on ' +
      'a 20-message thread in 30 seconds.',
    details: [
      'TL;DR, what the client wants, what we\'ve tried, open questions, recommended next step',
      'Cached per-session; auto-invalidates when a new reply arrives',
      'Costs ~$0.001 per summary (Haiku 4.5)',
    ],
  },
  {
    date: '2026-05-12',
    category: 'fix',
    title: 'No more "Checking sign-in…" flash on tab switch',
    body:
      'Switching tabs no longer flashes the auth loading screen or resets ' +
      'scroll position. Long ticket threads stay where you left them. ' +
      'Composer drafts, dialogs, and slash-menu state all survive tab ' +
      'switches now.',
  },
  {
    date: '2026-05-12',
    category: 'fix',
    title: 'Client replies to merged tickets now land on the target',
    body:
      'When two tickets were merged and the lead replied to the OLD Gmail ' +
      'thread, their reply was silently landing on the closed source ticket. ' +
      'Replies now follow the merge pointer and arrive on the live target ' +
      'ticket, with team replies routed back to the lead\'s thread.',
  },
  {
    date: '2026-05-12',
    category: 'feature',
    title: 'Team member status + invite emails',
    body:
      'Team members invited via the admin UI now get a styled invitation ' +
      'email automatically. Pending members (invited, not signed in yet) ' +
      'show a "Pending" badge and a "Resend invite" button. Once they sign ' +
      'in for the first time, the badge clears.',
    details: [
      'Active / Pending / Inactive tabs split a growing team list',
      'Pending count badge renders amber when there\'s outstanding work',
    ],
  },
  {
    date: '2026-05-11',
    category: 'feature',
    title: 'Compose dialog gets the rich body editor',
    body:
      'The "Compose new ticket" dialog now uses the same rich body editor ' +
      'as the reply composer — formatting toolbar (bold / italic / quote / ' +
      'lists / link / clear), inline image paste, slash-menu templates, ' +
      'and the Polish button all work the same way.',
  },
  {
    date: '2026-05-11',
    category: 'feature',
    title: 'Dashboard ticket sorting',
    body:
      'A new Sort dropdown next to the Views button lets you reorder the ' +
      'ticket list by Newest, Oldest, Latest reply, Priority, SLA due soonest, ' +
      'or Subject A–Z. SLA-due sort floats the tickets that need attention now.',
  },
  {
    date: '2026-05-10',
    category: 'feature',
    title: 'PWA install + offline-shell caching',
    body:
      'Install the admin UI as a desktop / mobile app via the "Install app" ' +
      'entry in the user dropdown. On iOS Safari, use Share → Add to Home Screen.',
  },
  {
    date: '2026-05-10',
    category: 'feature',
    title: 'Customer health snapshot on contact pages',
    body:
      'Every contact page now shows lifetime ticket count, open / resolved ' +
      'split, average time-to-resolution, SLA breach %, recent sentiment, ' +
      'and a green / amber / rose health badge.',
  },
  {
    date: '2026-05-10',
    category: 'feature',
    title: 'Reply tone check (Grammarly-style)',
    body:
      'While you type a reply, Claude Haiku quietly checks the tone. If ' +
      'it spots something passive-aggressive, curt, or dismissive, an ' +
      'advisory strip appears above the editor. Never blocks Send — ' +
      'purely a heads-up.',
  },
  {
    date: '2026-05-09',
    category: 'feature',
    title: 'Saved dashboard views',
    body:
      'Filter the dashboard however you like, then click Views → Save current ' +
      'view to capture it. One click later you\'re back at "My P1s" or ' +
      '"Stale waiting on client" or whatever you saved. Per-user, capped at 20.',
  },
  {
    date: '2026-05-09',
    category: 'feature',
    title: 'Inline KB suggestions in composer',
    body:
      'When a ticket has KB suggestions from triage, they show as small ' +
      'chips above the reply editor. Click → article body inserts at the ' +
      'caret, ready to edit before sending.',
  },
  {
    date: '2026-05-09',
    category: 'feature',
    title: 'Undo send · 5-second window',
    body:
      'Hit Send → toast appears with a 5-second Undo button. If you spot ' +
      'a typo or wrong recipient before the timer runs out, click Undo ' +
      'and your draft is untouched. Click Send again during the window ' +
      'to skip the wait.',
  },
  {
    date: '2026-05-09',
    category: 'feature',
    title: 'Daily morning digest email',
    body:
      'Weekdays at 08:00 Sydney, every team member with the digest enabled ' +
      'gets a summary of overnight tickets, their priority queue, and SLAs ' +
      'at risk today. Manage in Settings → Email notifications.',
  },
  {
    date: '2026-05-08',
    category: 'feature',
    title: 'Live SLA countdown on ticket detail',
    body:
      'Tickets now show a live-ticking SLA badge (First reply / Resolution) ' +
      'that turns amber under 30 minutes and rose when breached.',
  },
  {
    date: '2026-05-08',
    category: 'improvement',
    title: 'Auto-close bounce + out-of-office replies',
    body:
      'Mailer-daemon bounces and vacation auto-replies no longer create ' +
      'cluttered tickets. Workflow 01 sniffs them at the front door and ' +
      'logs them to system_events instead.',
  },
  {
    date: '2026-05-08',
    category: 'feature',
    title: '⌘K command palette + keyboard shortcuts',
    body:
      'Press ⌘K (or Ctrl+K) anywhere to fuzzy-search tickets, contacts, ' +
      'KB articles, and pages. Press ? to see all keyboard shortcuts.',
    details: [
      'On a ticket: r = focus reply · n = focus note · a = assign to me · e = mark resolved',
      'On dashboard: j/k = next/prev ticket · / = focus search · ⏎ = open',
    ],
  },
  {
    date: '2026-05-07',
    category: 'feature',
    title: 'Editable auto-follow-up day counts',
    body:
      'The day-N nudge and hard-close thresholds in workflow 05 are no ' +
      'longer hardcoded. Tweak them in Settings → Auto follow-up.',
  },
  {
    date: '2026-05-07',
    category: 'feature',
    title: 'Email notifications (per-user, per-type)',
    body:
      'Every in-app notification (client replied, SLA breach, you got ' +
      'reassigned, you were @-mentioned, …) can now also land in your ' +
      'email. Toggle each type independently in Settings → Email ' +
      'notifications.',
  },
  {
    date: '2026-05-07',
    category: 'feature',
    title: 'Admin / member roles + team groups',
    body:
      'Team members now have an admin / member role. Admins manage groups, ' +
      'KB, SLA rules, and other team members. The new Groups page lets you ' +
      'organize teammates into Sales / Delivery / Marketing or any custom ' +
      'grouping.',
  },
  {
    date: '2026-05-07',
    category: 'feature',
    title: 'Reports: filter by group or specific member',
    body:
      'Reports page gains Groups + Members filter pills. Pick a group to ' +
      'see the tickets that team handled; pick individuals to focus on ' +
      'their slice. Drill-downs carry the filter into the dashboard.',
  },
  {
    date: '2026-05-06',
    category: 'feature',
    title: 'Reply composer formatting toolbar',
    body:
      'Gmail-style toolbar above the reply editor: bold, italic, ' +
      'underline, strikethrough, inline code, bulleted + numbered lists, ' +
      'indent / outdent, quote, link, clear formatting.',
  },
];

/** Returns the date of the most recent entry — drives the unread dot. */
export function latestChangelogDate(): string {
  return CHANGELOG[0]?.date ?? '';
}

/** localStorage key for "user has acknowledged the changelog up to this date". */
export const CHANGELOG_LAST_SEEN_KEY = 'pa-changelog-last-seen';

/** Has the user seen the latest entry? Driven entirely off localStorage so
 *  there's no DB hit on every page load. Wiping localStorage = seeing the
 *  full unread list again, which is fine. */
export function hasUnreadChangelog(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const seen = window.localStorage.getItem(CHANGELOG_LAST_SEEN_KEY);
    if (!seen) return CHANGELOG.length > 0;
    return latestChangelogDate() > seen;
  } catch {
    return false;
  }
}

export function markChangelogSeen(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(CHANGELOG_LAST_SEEN_KEY, latestChangelogDate());
  } catch {
    // ignore — private mode / quota etc.
  }
}

/** Returns the entries posted strictly after the last-seen date. Used for
 *  the "New since you last visited" header on the changelog page. */
export function unreadEntries(): ChangelogEntry[] {
  if (typeof window === 'undefined') return [];
  try {
    const seen = window.localStorage.getItem(CHANGELOG_LAST_SEEN_KEY);
    if (!seen) return CHANGELOG.slice();
    return CHANGELOG.filter((e) => e.date > seen);
  } catch {
    return [];
  }
}
